import { useState, useCallback } from "react";
/**
 * Hook to manage flow interaction state and handlers
 *
 * Provides state management for node selection and edge highlighting.
 * When a node is clicked, it becomes selected and all connected edges are highlighted.
 *
 * @param edges - Array of flow edges to determine connections
 * @returns Interaction state and handler functions
 *
 * @example
 * ```tsx
 * const { selectedNodeId, highlightedEdges, handleNodeClick, clearSelection } =
 *   useFlowInteractions(edges);
 *
 * <ReactFlow
 *   nodes={nodes}
 *   edges={edges}
 *   onNodeClick={handleNodeClick}
 *   onPaneClick={clearSelection}
 * />
 * ```
 */
export function useFlowInteractions(edges) {
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [highlightedEdges, setHighlightedEdges] = useState([]);
    /**
     * Handle node click event
     * Selects the clicked node and highlights all connected edges
     */
    const handleNodeClick = useCallback((event, node) => {
        // Prevent event bubbling
        event.stopPropagation();
        // Set the selected node
        setSelectedNodeId(node.id);
        // Find all edges connected to this node (both incoming and outgoing)
        const connectedEdges = edges.filter((edge) => edge.source === node.id || edge.target === node.id);
        // Set highlighted edge IDs
        setHighlightedEdges(connectedEdges.map((edge) => edge.id));
    }, [edges]);
    /**
     * Clear selection state
     * Resets both selected node and highlighted edges
     */
    const clearSelection = useCallback(() => {
        setSelectedNodeId(null);
        setHighlightedEdges([]);
    }, []);
    return {
        selectedNodeId,
        highlightedEdges,
        handleNodeClick,
        clearSelection,
    };
}
